import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { LanguageProvider } from './context/LanguageContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import BottomMobileBar from './components/BottomMobileBar';
import SitePopup from './components/SitePopup';
import CookieBanner from './components/CookieBanner';
import ScrollToTop from './components/ScrollToTop';
// import WebsiteGate from './components/WebsiteGate';
import PageBox from './components/PageBox';
import FloatingChatButton from './components/FloatingChatButton';
import HomePage from './pages/HomePage';
import DynamicPage from './pages/DynamicPage';
import CategoryPage from './pages/CategoryPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ServicesPage from './pages/ServicesPage';
import AboutUsPage from './pages/AboutUsPage';
import CatalogPage from './pages/CatalogPage';
import MyAccountPage from './pages/MyAccountPage';
import CatalogCategoryPage from './pages/CatalogCategoryPage';
import FAQPage from './pages/FAQPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import SearchResultsPage from './pages/SearchResultsPage';
import ContactPage from './pages/ContactPage';
import BrandsPage from './pages/BrandsPage';
import BrandPage from './pages/BrandPage';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/admin/AdminLogin';
import ContentManagement from './pages/admin/ContentManagement';
import Dashboard from './pages/admin/Dashboard';
import ProductsManagement from './pages/admin/ProductsManagement';
import CategoriesManagement from './pages/admin/CategoriesManagement';
import BrandsManagement from './pages/admin/BrandsManagement';
import UsersManagement from './pages/admin/UsersManagement';
import OrdersManagement from './pages/admin/OrdersManagement';
import RequestsManagement from './pages/admin/RequestsManagement';
import Settings from './pages/admin/Settings';
import Pages from './pages/admin/Pages';
import GiftsManagement from './pages/admin/GiftsManagement';
import GiftConditionsManagement from './pages/admin/GiftConditionsManagement';
import PopupManagement from './pages/admin/PopupManagement';
import { Toaster } from './components/ui/toaster';
import './App.css';

const RouteChangeHandler = ({ children }) => {
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { isAdminAuthenticated, loading } = useAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

const PublicBottomMobileBar = () => {
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <BottomMobileBar
      wishlistCount={0}
      cartCount={0}
      onMenuClick={() => {
        window.dispatchEvent(new CustomEvent('open-mobile-menu'));
      }}
    />
  );
};

const PublicSitePopup = () => {
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return <SitePopup />;
};

function App() {
  const createDualRoutes = (path, element) => {
    return (
      <>
        <Route path={path} element={element} />
        <Route path={`/ru${path}`} element={element} />
      </>
    );
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminProvider>
          <LanguageProvider>
            <CartProvider>
              <div className="App pb-[59px] md:pb-0">
                <ScrollToTop />

                <RouteChangeHandler>
                  {/* <WebsiteGate> */}
                  <Routes>
                    {createDualRoutes(
                      '/',
                      <>
                        <Navbar />
                        <PageBox>
                        <HomePage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/contact',
                      <>
                        <Navbar />
                        <PageBox>
                        <ContactPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/brands',
                      <>
                        <Navbar />
                        <PageBox>
                        <BrandsPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/brand/:slug',
                      <>
                        <Navbar />
                        <PageBox>
                        <BrandPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/search',
                      <>
                        <Navbar />
                        <PageBox>
                        <SearchResultsPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/page/:slug',
                      <>
                        <Navbar />
                        <PageBox>
                        <DynamicPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/category/:slug',
                      <>
                        <Navbar />
                        <PageBox>
                        <CategoryPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/product/:id',
                      <>
                        <Navbar />
                        <PageBox>
                        <ProductDetailPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/cart',
                      <>
                        <Navbar />
                        <PageBox>
                        <CartPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/checkout',
                      <>
                        <Navbar />
                        <PageBox>
                        <CheckoutPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/servicii',
                      <>
                        <Navbar />
                        <PageBox>
                        <ServicesPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/despre-noi',
                      <>
                        <Navbar />
                        <PageBox>
                        <AboutUsPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/catalog',
                      <>
                        <Navbar />
                        <PageBox>
                        <CatalogPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/catalog/:categoryId',
                      <>
                        <Navbar />
                        <PageBox>
                        <CatalogCategoryPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/intrebari-frecvente',
                      <>
                        <Navbar />
                        <PageBox>
                        <FAQPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/order-success',
                      <>
                        <Navbar />
                        <PageBox>
                        <OrderSuccessPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    {createDualRoutes(
                      '/contul-meu',
                      <>
                        <Navbar />
                        <PageBox>
                        <MyAccountPage />
                        </PageBox>
                        <Footer />
                      </>
                    )}

                    <Route path="/admin/login" element={<AdminLogin />} />

                    <Route
                      path="/admin/dashboard"
                      element={
                        <AdminProtectedRoute>
                          <Dashboard />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/products"
                      element={
                        <AdminProtectedRoute>
                          <ProductsManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/categories"
                      element={
                        <AdminProtectedRoute>
                          <CategoriesManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/brands"
                      element={
                        <AdminProtectedRoute>
                          <BrandsManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/users"
                      element={
                        <AdminProtectedRoute>
                          <UsersManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/orders"
                      element={
                        <AdminProtectedRoute>
                          <OrdersManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/settings"
                      element={
                        <AdminProtectedRoute>
                          <Settings />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/requests"
                      element={
                        <AdminProtectedRoute>
                          <RequestsManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/content"
                      element={
                        <AdminProtectedRoute>
                          <ContentManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/pages"
                      element={
                        <AdminProtectedRoute>
                          <Pages />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/gifts"
                      element={
                        <AdminProtectedRoute>
                          <GiftsManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/gift-conditions"
                      element={
                        <AdminProtectedRoute>
                          <GiftConditionsManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/popup"
                      element={
                        <AdminProtectedRoute>
                          <PopupManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin"
                      element={<Navigate to="/admin/dashboard" replace />}
                    />

                    <Route
                      path="*"
                      element={
                        <>
                          <Navbar />
                          <PageBox>
                          <NotFound />
                          </PageBox>
                          <Footer />
                        </>
                      }
                    />
                  </Routes>
                  <FloatingChatButton />
                  {/* </WebsiteGate> */}
                  <CookieBanner />
                </RouteChangeHandler>

                <PublicSitePopup />
                <PublicBottomMobileBar />
                <Toaster />
              </div>
            </CartProvider>
          </LanguageProvider>
        </AdminProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;